import {createSelector} from '@ngrx/store';
import {selectAssignments} from '@assignment/store/assignment.selectors';
import {selectMembers} from '@member/store/member.selectors';
import {selectStations} from '@station/store/station.selectors';
import {selectClients} from '@client/store/client.selectors';
import {DateTime} from 'luxon';
import {AssignmentDto, CalendarEventDto, ClientDto, EnEventStatus, StationDto, UserDto} from '@shared/dto';
import {selectUsers} from '@user/store/user.selectors';
import {selectEvents} from '@event/store/event.selectors';

const DURATION = { month: 1 };

/** Statuses that flag an event as needing the practitioner's attention. */
const NEED_ATTENTION_STATUSES: ReadonlySet<EnEventStatus> = new Set([
  EnEventStatus.MISSED,
  EnEventStatus.SKIPPED,
  EnEventStatus.DELAYED,
]);

/** Parses an event `start` (ISO string in the lean calendar DTO). */
function eventStart(event: CalendarEventDto): DateTime {
  const raw = event.start as unknown;
  return raw instanceof Date ? DateTime.fromJSDate(raw) : DateTime.fromISO(raw as string);
}

export const selectUsersCount = createSelector(
  selectUsers,
  (users: UserDto[] | null) => users ? users.filter(c => !c.deleted).length : 0
);

export const selectUsersArchivedCount = createSelector(
  selectUsers,
  (users: UserDto[] | null) => users ? users.filter(c => !!c.deleted).length : 0
);

export const selectClientsCount = createSelector(
  selectClients,
  (clients: ClientDto[] | null) => clients ? clients.filter(c => !c.deleted).length : 0
);

export const selectClientsArchivedCount = createSelector(
  selectClients,
  (clients: ClientDto[] | null) => clients ? clients.filter(c => !!c.deleted).length : 0
);

export const selectStationCount = createSelector(
  selectStations,
  (stations: StationDto[] | null) => stations ? stations.filter(s => !s.deleted).length : 0
);

export const selectStationsArchivedCount = createSelector(
  selectStations,
  (stations: StationDto[] | null) => stations ? stations.filter(s => !!s.deleted).length : 0
);

export const selectMembersCount = createSelector(
  selectMembers,
  (members) => members ? members.filter(m => !m.deleted).length : 0
);

export const selectMembersArchivedCount = createSelector(
  selectMembers,
  (members) => members ? members.filter(m => !!m.deleted).length : 0
);

export const selectAssignmentsCompleted = createSelector(
  selectAssignments,
  (assignments: AssignmentDto[] | null) => {
    if (!assignments) return 0;
    return assignments.filter(therapy =>
      therapy.status === EnEventStatus.COMPLETED
    ).length;
  }
);

export const selectAssignmentsCompletedPrevious = createSelector(
  selectAssignments,
  (assignments: AssignmentDto[] | null) => {
    if (!assignments) return 0;
    const from = DateTime.now().minus(DURATION);
    return assignments.filter(therapy =>
      DateTime.fromISO(therapy.created) < from &&
      therapy.status === EnEventStatus.COMPLETED
    ).length;
  }
);

export const selectAssignmentsAssigned = createSelector(
  selectAssignments,
  (assignments: AssignmentDto[] | null) => {
    if (!assignments) return 0;
    return assignments.length;
  }
);

export const selectAssignmentsAssignedPrevious = createSelector(
  selectAssignments,
  (assignments: AssignmentDto[] | null) => {
    if (!assignments) return 0;
    const from = DateTime.now().minus(DURATION);
    return assignments.filter(therapy =>
      DateTime.fromISO(therapy.created) < from
    ).length;
  }
);

// --- Dashboard summary cards (driven by the operator-wide event store) ---------

/** Number of events scheduled for today. */
export const selectTodaysEventsCount = createSelector(
  selectEvents,
  (events: CalendarEventDto[] | null) => {
    if (!events) return 0;
    const today = DateTime.now();
    return events.filter(event => eventStart(event).hasSame(today, 'day')).length;
  }
);

/** Distinct patients that have at least one event in the loaded range. */
export const selectActivePatientsCount = createSelector(
  selectEvents,
  (events: CalendarEventDto[] | null) => {
    if (!events) return 0;
    const patients = new Set<number>();
    events.forEach(event => {
      const id = event.userId;
      if (id != null) patients.add(id);
    });
    return patients.size;
  }
);

/** Events flagged as needing attention (missed / skipped / delayed). */
export const selectNeedAttentionCount = createSelector(
  selectEvents,
  (events: CalendarEventDto[] | null) => {
    if (!events) return 0;
    return events.filter(event => NEED_ATTENTION_STATUSES.has(event.status)).length;
  }
);
